/**
 * azureDiagram.ts - assemble le schema d architecture Azure a partir d un dataset.
 *
 * Le rendu (disposition, cartes, liens SVG, filtres, panneau de details, export
 * PNG) est celui de l outil HYBEOS AzureDescriptor : on reutilise son gabarit
 * autonome et on se contente de remplir ses quatre emplacements. C est le port
 * TypeScript de Modules/AzureDescriptor.Diagram.psm1 (New-AzdDiagram).
 *
 * Les trois assets sont charges a la demande (fetch sur leur URL Vite) et non
 * embarques dans le bundle : ~330 Ko au total, seulement quand l utilisateur
 * ouvre la page Schema.
 */
import templateUrl from '../assets/azure-diagram/template.html?url';
import iconsUrl from '../assets/azure-diagram/icons.svg?url';
import iconMapUrl from '../assets/azure-diagram/icon-map.json?url';

import type { AzureTopologyDataset } from '../api/azureTopologyApi';

/** Marqueurs poses dans icons.svg a la generation du sprite. */
const SYMBOLS_START = '<!--symbols-start-->';
const SYMBOLS_END   = '<!--symbols-end-->';

interface DiagramAssets {
  template: string;
  /** Sprite <svg><defs><symbol id="ico-..."> ... — insere tel quel dans la page. */
  icons:    string;
  /** Cle de type ARM en minuscules -> id de symbole. */
  iconMap:  Record<string, string>;
}

let assetsPromise: Promise<DiagramAssets> | null = null;

/** Charge (une seule fois par session) le gabarit, le sprite d icones et la table de correspondance. */
export function loadDiagramAssets(): Promise<DiagramAssets> {
  if (!assetsPromise) {
    assetsPromise = Promise.all([
      fetch(templateUrl).then((r) => r.text()),
      fetch(iconsUrl).then((r) => r.text()),
      fetch(iconMapUrl).then((r) => r.json() as Promise<Record<string, string>>),
    ])
      .then(([template, icons, iconMap]) => ({ template, icons, iconMap }))
      .catch((e) => { assetsPromise = null; throw e; });
  }
  return assetsPromise;
}

/**
 * Resout l id de symbole d un type de ressource.
 * Meme cascade que Get-AzdIconName : surcharge par `kind`, correspondance exacte,
 * puis remontee du type parent (a/b/c -> a/b -> a), puis defaut.
 */
function resolveIcon(iconMap: Record<string, string>, type: string, kind?: string): string {
  const t = (type ?? '').toLowerCase();
  const k = (kind ?? '').toLowerCase();
  if (k) {
    for (const frag of ['functionapp', 'frontdoor']) {
      if (k.includes(frag) && iconMap[`${t}|${frag}`]) return iconMap[`${t}|${frag}`];
    }
  }
  if (iconMap[t]) return iconMap[t];
  let cur = t;
  while (cur.includes('/')) {
    cur = cur.slice(0, cur.lastIndexOf('/'));
    if (iconMap[cur]) return iconMap[cur];
    if (iconMap[`${cur}/`]) return iconMap[`${cur}/`];
  }
  return iconMap['default'];
}

/** Table type -> symbole reellement utilisee par la page, limitee aux types presents. */
function buildUsedMap(iconMap: Record<string, string>, ds: AzureTopologyDataset): Record<string, string> {
  const used: Record<string, string> = {};
  for (const key of ['subscription', 'resourcegroup', 'subnet', 'default', 'lock', 'policy', 'advisor', 'external']) {
    if (iconMap[key]) used[key] = iconMap[key];
  }
  for (const r of ds.resources ?? []) {
    const type = String(r.type ?? '');
    if (!type) continue;
    const kind = String((r as { kind?: string }).kind ?? '').toLowerCase();
    let key = type.toLowerCase();
    for (const frag of ['functionapp', 'frontdoor']) {
      if (kind.includes(frag) && iconMap[`${key}|${frag}`]) key = `${key}|${frag}`;
    }
    if (used[key]) continue;
    used[key] = resolveIcon(iconMap, type, kind);
  }
  return used;
}

/** Echappe ce qui casserait le document hote une fois injecte dans un <script>. */
function safeJson(value: unknown): string {
  // On neutralise aussi bien l ouverture que la FERMETURE d un commentaire HTML :
  // une valeur ARM contenant --> (une description, une requete, un fragment de
  // config) refermerait prematurement tout commentaire environnant.
  return JSON.stringify(value)
    .replace(/<\/script/gi, '<\\/script')
    .replace(/<!--/g, '<\\!--')
    .replace(/--!?>/g, '--\\u003e');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export interface BuildDiagramOptions {
  /** Langue de l interface du schema. */
  language?: 'FR' | 'EN';
  /** Titre affiche dans la barre du schema (defaut : "Azure - <nom d abonnement>"). */
  title?:    string;
  /** Date affichee en pied du cadre d abonnement (defaut : maintenant). */
  generated?: Date;
}

/**
 * Produit le document HTML autonome du schema.
 * Destine a un <iframe srcDoc> ou a un telechargement .html.
 */
export function buildDiagramHtml(
  assets: DiagramAssets,
  dataset: AzureTopologyDataset,
  opts: BuildDiagramOptions = {},
): string {
  const language = opts.language ?? 'FR';
  const subName  = dataset.meta?.subscriptionName || dataset.meta?.subscriptionId || 'Azure';
  const title    = opts.title ?? `Azure - ${subName}`;
  const gen      = opts.generated ?? new Date();
  const stamp    = `${gen.getFullYear()}-${String(gen.getMonth() + 1).padStart(2, '0')}-${String(gen.getDate()).padStart(2, '0')} `
                 + `${String(gen.getHours()).padStart(2, '0')}:${String(gen.getMinutes()).padStart(2, '0')}`;

  // Le gabarit contient deja son propre <svg style="display:none"><defs> : on
  // n injecte que les <symbol>. Les icones portent elles-memes des <defs>
  // (degrades, masques), donc on decoupe sur les marqueurs poses a la
  // generation du sprite et non sur la premiere balise fermante rencontree.
  const start = assets.icons.indexOf(SYMBOLS_START);
  const end   = assets.icons.indexOf(SYMBOLS_END);
  if (start < 0 || end < 0) throw new Error('Sprite d icones Azure illisible : marqueurs absents.');
  const symbols = assets.icons.slice(start + SYMBOLS_START.length, end);

  // replaceAll, pas replace : un emplacement peut apparaitre plusieurs fois dans
  // le gabarit. Et remplacement par FONCTION : sinon les sequences $& / $1
  // presentes dans un nom de ressource ou une valeur ARM seraient interpretees.
  const put = (src: string, needle: string, value: string) => src.replaceAll(needle, () => value);

  let out = assets.template;
  out = put(out, '<!--__ICONS__-->', symbols);
  out = put(out, '/*__ICONMAP__*/', safeJson(buildUsedMap(assets.iconMap, dataset)));
  out = put(out, '/*__DATA__*/', safeJson(dataset));
  out = put(out, '__TITLE__', escapeHtml(title));
  out = put(out, '__LANG__', language);
  out = put(out, '__GENERATED__', stamp);
  return out;
}

/** Nom de fichier propose au telechargement du schema. */
export function diagramFileName(dataset: AzureTopologyDataset, generatedAt?: string): string {
  const sub  = String(dataset.meta?.subscriptionName ?? 'azure').replace(/[^\w.-]+/g, '_');
  const date = (generatedAt ?? new Date().toISOString()).slice(0, 10);
  return `NIS365_Schema_Azure_${sub}_${date}.html`;
}
